import { Injectable } from '@angular/core';
import { Client, IMessage, ReconnectionTimeMode } from '@stomp/stompjs';
import { Subject } from 'rxjs';
import { environment } from '../../../../environment/environment';
import { CallSignal } from './quick-chat.types';

@Injectable({
    providedIn: 'root',
})
export class WebrtcService {
    private peer: RTCPeerConnection | null = null;
    private localStream: MediaStream | null = null;
    private remoteStream: MediaStream | null = null;
    private cameraVideoTrack: MediaStreamTrack | null = null;
    private screenStream: MediaStream | null = null;
    private videoSender: RTCRtpSender | null = null;
    private stompClient: Client | null = null;
    private pendingIceCandidates: RTCIceCandidate[] = [];
    private currentUserId: number | null = null;
    private currentConversationId: number | null = null;
    private currentReceiverId: number | null = null;

    public incomingCall$ = new Subject<CallSignal>();
    public answeringCall$ = new Subject<CallSignal>();
    public connectionState$ = new Subject<RTCPeerConnectionState>();
    public callEnded$ = new Subject<CallSignal>();
    public offer$ = new Subject<CallSignal>();
    public answer$ = new Subject<CallSignal>();
    public iceCandidate$ = new Subject<CallSignal>();

    init(userId: number, token?: string): void {
        this.currentUserId = userId;

        if (this.stompClient) {
            return;
        }

        this.stompClient = new Client({
            brokerURL: environment.WS,
            // Service de chat indisponible : on réessaie avec un délai croissant
            // (5 s, 10 s, 20 s… plafonné à 5 min) au lieu de saturer la console.
            reconnectDelay: 5000,
            reconnectTimeMode: ReconnectionTimeMode.EXPONENTIAL,
            maxReconnectDelay: 5 * 60 * 1000,
            connectHeaders: {
                userId: String(userId),
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        });
        this.stompClient.activate();
    }

    listenToSignals(userId: number): void {
        this.currentUserId = userId;

        this.ensureSocket();
        this.stompClient!.onConnect = () => {
            this.stompClient!.subscribe(`/topic/call/${userId}`, (msg: IMessage) => {
                void this.handleSignalMessage(msg);
            });
        };

        if (this.stompClient.connected) {
            this.stompClient.subscribe(`/topic/call/${userId}`, (msg: IMessage) => {
                void this.handleSignalMessage(msg);
            });
        }
    }

    cleanUp(disconnectSignaling = true): void {
        if (disconnectSignaling && this.stompClient?.active) {
            this.stompClient.deactivate();
        }

        this.localStream?.getTracks().forEach((track) => track.stop());
        this.remoteStream?.getTracks().forEach((track) => track.stop());
        this.peer?.close();
        this.peer = null;
        this.localStream = null;
        this.remoteStream = null;
        this.pendingIceCandidates = [];
        this.currentConversationId = null;
        this.currentReceiverId = null;
        this.connectionState$.next('closed');
    }

    async initPeer(
        mediaType: 'audio' | 'video',
        onRemoteStream: (stream: MediaStream) => void,
    ): Promise<void> {
        const iceServers = environment.iceServers?.length
            ? environment.iceServers
            : [{ urls: 'stun:stun.l.google.com:19302' }];
        const hasTurnServer = iceServers.some((server) => {
            const urls = Array.isArray(server.urls) ? server.urls : [server.urls];
            return urls.some((url) => String(url).startsWith('turn'));
        });

        this.peer = new RTCPeerConnection({
            iceServers,
            iceTransportPolicy: hasTurnServer ? 'relay' : 'all',
            bundlePolicy: 'max-bundle',
            rtcpMuxPolicy: 'require',
        });

        this.localStream = await navigator.mediaDevices.getUserMedia({
            audio: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
            },
            video:
                mediaType === 'video'
                    ? {
                          width: { ideal: 1280 },
                          height: { ideal: 720 },
                          facingMode: 'user',
                      }
                    : false,
        });

        this.remoteStream = new MediaStream();
        this.cameraVideoTrack = this.localStream.getVideoTracks()[0] ?? null;
        this.videoSender = null;

        const peer = this.getPeer();
        const localStream = this.localStream;
        localStream.getTracks().forEach((track) => {
            const sender = peer.addTrack(track, localStream);
            if (track.kind === 'video') {
                this.videoSender = sender;
            }
        });

        peer.ontrack = (event) => {
            const [browserRemoteStream] = event.streams;
            if (browserRemoteStream) {
                this.remoteStream = browserRemoteStream;
                onRemoteStream(browserRemoteStream);
                return;
            }

            if (!this.remoteStream) {
                this.remoteStream = new MediaStream();
            }

            if (!event.track) {
                return;
            }

            this.remoteStream.addTrack(event.track);
            onRemoteStream(this.remoteStream);
        };

        peer.onconnectionstatechange = () => {
            this.connectionState$.next(peer.connectionState);
        };

        peer.onicecandidate = (event) => {
            if (!event.candidate || this.currentUserId == null) {
                return;
            }

            this.publishSignal({
                signalType: 'ICE',
                senderId: this.currentUserId,
                receiverId: this.currentReceiverId ?? undefined,
                conversationId: this.currentConversationId ?? undefined,
                candidate: JSON.stringify(event.candidate.toJSON()),
                createdAtEpochMs: Date.now(),
            });
        };
    }

    async createOffer(conversationId: number, receiverId: number, mediaType: 'audio' | 'video'): Promise<void> {
        const peer = this.getPeer();
        this.currentConversationId = conversationId;
        this.currentReceiverId = receiverId;
        if (peer.signalingState !== 'stable') {
            return;
        }

        const offer = await peer.createOffer({
            offerToReceiveAudio: true,
            offerToReceiveVideo: mediaType === 'video',
        });

        await peer.setLocalDescription(offer);
        this.publishSignal({
            signalType: 'OFFER',
            senderId: this.currentUserId ?? 0,
            receiverId,
            conversationId,
            sdp: offer.sdp || '',
            mediaType,
            createdAtEpochMs: Date.now(),
        });
        this.offer$.next({
            signalType: 'OFFER',
            senderId: this.currentUserId ?? 0,
            receiverId,
            conversationId,
            sdp: offer.sdp || '',
            mediaType,
            createdAtEpochMs: Date.now(),
        });
    }

    async createAnswer(signal: CallSignal): Promise<void> {
        const peer = this.getPeer();
        if (!signal.sdp) {
            throw new Error('Missing SDP offer');
        }

        this.currentConversationId = signal.conversationId ?? this.currentConversationId;
        this.currentReceiverId = signal.senderId ?? this.currentReceiverId;

        if (peer.remoteDescription || peer.signalingState !== 'stable') {
            return;
        }

        await peer.setRemoteDescription({ type: 'offer', sdp: signal.sdp });
        const answer = await peer.createAnswer();
        await peer.setLocalDescription(answer);
        await this.flushPendingIceCandidates();
        this.publishSignal({
            signalType: 'ANSWER',
            senderId: this.currentUserId ?? 0,
            receiverId: signal.senderId ?? 0,
            conversationId: signal.conversationId,
            sdp: answer.sdp || '',
            mediaType: signal.mediaType,
            createdAtEpochMs: Date.now(),
        });
        this.answeringCall$.next(signal);
    }

    async endCall(signal: Partial<CallSignal>): Promise<void> {
        const receiverId = signal.receiverId ?? this.currentReceiverId ?? undefined;
        const conversationId = signal.conversationId ?? this.currentConversationId ?? undefined;

        if (this.currentUserId != null) {
            this.publishSignal({
                signalType: 'END',
                senderId: this.currentUserId,
                receiverId,
                conversationId,
                callCode: signal.callCode,
                createdAtEpochMs: Date.now(),
            });
        }

        this.cleanUp(false);
        this.callEnded$.next({
            signalType: 'END',
            senderId: signal.senderId,
            receiverId,
            conversationId,
            callCode: signal.callCode,
        });
    }

    async handleSignalMessage(msg: IMessage): Promise<void> {
        const signal = JSON.parse(msg.body) as CallSignal;

        if (signal.signalType === 'OFFER') {
            this.incomingCall$.next(signal);
            return;
        }

        if (signal.signalType === 'ANSWER') {
            await this.setAnswer(signal);
            return;
        }

        if (signal.signalType === 'ICE' && signal.candidate) {
            const candidate = new RTCIceCandidate(JSON.parse(signal.candidate));
            if (!this.canAcceptIceCandidates()) {
                this.pendingIceCandidates.push(candidate);
                this.iceCandidate$.next(signal);
                return;
            }

            await this.getPeer().addIceCandidate(candidate);
            this.iceCandidate$.next(signal);
            return;
        }

        if (signal.signalType === 'END') {
            this.callEnded$.next(signal);
            this.cleanUp(false);
        }
    }

    async setAnswer(signal: CallSignal): Promise<void> {
        if (!signal.sdp) {
            return;
        }

        const peer = this.getPeer();
        if (peer.remoteDescription || peer.signalingState !== 'have-local-offer') {
            return;
        }

        await peer.setRemoteDescription({ type: 'answer', sdp: signal.sdp });
        await this.flushPendingIceCandidates();
        this.answer$.next(signal);
    }

    getLocalStream(): MediaStream | null {
        return this.localStream;
    }

    getRemoteStream(): MediaStream | null {
        return this.remoteStream;
    }

    hasLocalStream(): boolean {
        return Boolean(this.localStream);
    }

    isMicrophoneEnabled(): boolean {
        return Boolean(this.localStream && this.localStream.getAudioTracks().some((track) => track.enabled));
    }

    isCameraEnabled(): boolean {
        return Boolean(this.localStream && this.localStream.getVideoTracks().some((track) => track.enabled));
    }

    setMicrophoneEnabled(enabled: boolean): void {
        this.localStream?.getAudioTracks().forEach((track) => (track.enabled = enabled));
    }

    setCameraEnabled(enabled: boolean): void {
        this.localStream?.getVideoTracks().forEach((track) => (track.enabled = enabled));
    }

    async startScreenShare(): Promise<boolean> {
        if (!this.videoSender || !this.peer || !this.localStream || this.localStream.getVideoTracks().length === 0) {
            return false;
        }

        if (this.screenStream) {
            return true;
        }

        const screenStream = await navigator.mediaDevices.getDisplayMedia({
            video: true,
            audio: false,
        });

        const screenTrack = screenStream.getVideoTracks()[0];
        if (!screenTrack) {
            screenStream.getTracks().forEach((track) => track.stop());
            return false;
        }

        this.screenStream = screenStream;
        await this.videoSender.replaceTrack(screenTrack);
        screenTrack.onended = () => {
            void this.stopScreenShare();
        };

        return true;
    }

    async stopScreenShare(): Promise<void> {
        if (this.videoSender && this.cameraVideoTrack) {
            await this.videoSender.replaceTrack(this.cameraVideoTrack);
        }

        this.screenStream?.getTracks().forEach((track) => track.stop());
        this.screenStream = null;
    }

    isScreenSharing(): boolean {
        return Boolean(this.screenStream);
    }

    getScreenStream(): MediaStream | null {
        return this.screenStream;
    }

    private ensureSocket(): void {
        if (!this.stompClient) {
            throw new Error('WebRTC signaling client not initialized. Call init() first.');
        }
    }

    private getPeer(): RTCPeerConnection {
        if (!this.peer) {
            throw new Error('WebRTC peer not initialized.');
        }

        return this.peer;
    }

    private canAcceptIceCandidates(): boolean {
        return Boolean(this.peer && this.peer.connectionState !== 'closed' && this.peer.remoteDescription);
    }

    private async flushPendingIceCandidates(): Promise<void> {
        if (!this.canAcceptIceCandidates() || !this.pendingIceCandidates.length) {
            return;
        }

        const pending = [...this.pendingIceCandidates];
        this.pendingIceCandidates = [];
        for (const candidate of pending) {
            await this.getPeer().addIceCandidate(candidate);
        }
    }

    private publishSignal(signal: CallSignal): void {
        this.ensureSocket();
        const destination =
            signal.signalType === 'OFFER'
                ? '/app/call.offer'
                : signal.signalType === 'ANSWER'
                    ? '/app/call.answer'
                    : signal.signalType === 'ICE'
                        ? '/app/call.ice'
                        : '/app/call.end';

        this.stompClient!.publish({
            destination,
            body: JSON.stringify(signal),
        });
    }
}
